import { FilterFilled } from "@ant-design/icons";
import { ColumnsType } from "antd/es/table";
import { ProductionAnalysisItem } from "shared/api";
import { dayjs } from "shared/helpers";
import {
  DatePickerValue,
  DateRange,
  TableSearchFilter,
  TableRadioGroup,
} from "shared/ui";
import { AdaptedValues } from "shared/types";

interface UseProductionAnalysisTableColumnsProps {
  orderCodeValue: string;
  handleOrderCodeValue: (e: React.ChangeEvent<HTMLInputElement>) => void;
  clearOrderCodeValue: () => void;
  handleDateGreater: (date: DatePickerValue, dateString: string) => void;
  handleDateLesser: (date: DatePickerValue, dateString: string) => void;
  isDateFilterActive: boolean;
  machineFilter: number | string;
  handleMachineFilter: (value: number | string) => void;
  adaptedMachines: AdaptedValues[];
  userFilter: number | string;
  handleUserFilter: (value: number | string) => void;
  adaptedUsers: AdaptedValues[];
  speedMinValue: string;
  handleSpeedMinValue: (e: React.ChangeEvent<HTMLInputElement>) => void;
  clearSpeedMinValue: () => void;
  speedMaxValue: string;
  handleSpeedMaxValue: (e: React.ChangeEvent<HTMLInputElement>) => void;
  clearSpeedMaxValue: () => void;
}

export const useProductionAnalysisTableColumns = ({
  orderCodeValue,
  handleOrderCodeValue,
  clearOrderCodeValue,
  handleDateGreater,
  handleDateLesser,
  isDateFilterActive,
  machineFilter,
  handleMachineFilter,
  adaptedMachines,
  userFilter,
  handleUserFilter,
  adaptedUsers,
  speedMinValue,
  handleSpeedMinValue,
  clearSpeedMinValue,
  speedMaxValue,
  handleSpeedMaxValue,
  clearSpeedMaxValue,
}: UseProductionAnalysisTableColumnsProps): ColumnsType<ProductionAnalysisItem> => {
  const timezone = localStorage.getItem("timezone") || "UTC";

  return [
    {
      title: "Order",
      render: (record: ProductionAnalysisItem) =>
        record?.order_process_step_tracker?.order?.order_code || "N/A",
      filterDropdown: ({ setSelectedKeys, confirm, visible }) => (
        <TableSearchFilter
          setSelectedKeys={setSelectedKeys}
          clearFilters={clearOrderCodeValue}
          confirm={confirm}
          value={orderCodeValue}
          handleFilterChange={handleOrderCodeValue}
          visible={visible}
          placeholder="Filter by Order Code"
          title="Order Code"
        />
      ),
      filterIcon: (
        <FilterFilled
          className={orderCodeValue.length ? "!text-blue-600" : ""}
        />
      ),
    },
    {
      title: "Step",
      render: (record: ProductionAnalysisItem) =>
        record?.order_process_step_tracker?.process_step?.name || "N/A",
    },
    {
      title: "Machine",
      render: (record: ProductionAnalysisItem) =>
        record?.machine?.name || "N/A",
      filterDropdown: () => (
        <TableRadioGroup
          options={adaptedMachines}
          selectedValue={machineFilter}
          onChange={handleMachineFilter}
        />
      ),
      filterIcon: (
        <FilterFilled className={machineFilter ? "!text-blue-600" : ""} />
      ),
    },
    {
      title: "User",
      render: (record: ProductionAnalysisItem) =>
        record?.user
          ? `${record.user.first_name} ${record.user.last_name}`
          : "N/A",
      filterDropdown: () => (
        <TableRadioGroup
          options={adaptedUsers}
          selectedValue={userFilter}
          onChange={handleUserFilter}
        />
      ),
      filterIcon: (
        <FilterFilled className={userFilter ? "!text-blue-600" : ""} />
      ),
    },
    {
      title: "Timestamp",
      render: (record: ProductionAnalysisItem) =>
        record?.timestamp
          ? dayjs(record.timestamp).tz(timezone).format("YYYY-MM-DD HH:mm")
          : "N/A",
      filterIcon: (
        <FilterFilled className={isDateFilterActive ? "!text-red-600" : ""} />
      ),
      filterDropdown: () => {
        return (
          <DateRange
            title="Timestamp"
            leftPickerTitle="From"
            rightPickerTitle="To"
            handlePickerChangeGreater={handleDateGreater}
            handlePickerChangeLesser={handleDateLesser}
          />
        );
      },
    },
    {
      title: "Speed",
      render: (record: ProductionAnalysisItem) =>
        record?.detected_speed !== null
          ? Math.floor(record.detected_speed)
          : "N/A",
      filterDropdown: ({ setSelectedKeys, confirm, visible }) => (
        <div className="flex-col p-3">
          <div className="mb-2">
            <TableSearchFilter
              setSelectedKeys={setSelectedKeys}
              clearFilters={clearSpeedMinValue}
              confirm={confirm}
              value={speedMinValue}
              handleFilterChange={handleSpeedMinValue}
              visible={visible}
              placeholder="Min Speed"
              title="Min Speed"
            />
          </div>
          <TableSearchFilter
            setSelectedKeys={setSelectedKeys}
            clearFilters={clearSpeedMaxValue}
            confirm={confirm}
            value={speedMaxValue}
            handleFilterChange={handleSpeedMaxValue}
            visible={visible}
            placeholder="Max Speed"
            title="Max Speed"
          />
        </div>
      ),
      filterIcon: (
        <FilterFilled
          className={
            speedMinValue.length || speedMaxValue.length ? "!text-blue-600" : ""
          }
        />
      ),
    },
    {
      title: "Target Speed",
      render: (record: ProductionAnalysisItem) =>
        record?.detected_target_speed !== null
          ? Math.floor(record.detected_target_speed)
          : "N/A",
    },
    {
      title: "Start",
      render: (record: ProductionAnalysisItem) =>
        record?.detected_start_timestamp
          ? dayjs(record.detected_start_timestamp)
              .tz(timezone)
              .format("YYYY-MM-DD HH:mm")
          : "N/A",
    },
    {
      title: "Stop",
      render: (record: ProductionAnalysisItem) =>
        record?.detected_stop_timestamp
          ? dayjs(record.detected_stop_timestamp)
              .tz(timezone)
              .format("YYYY-MM-DD HH:mm")
          : "N/A",
    },
  ];
};
